import { useState, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import ConnectionDetailPage from './ConnectionDetailPage';
import { SHADOWS, DETAIL_TOKENS, COLORS } from '../../styles/UISystem';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import NameInput from '../UISystem/Forms/NameInput';
import ContactFields from '../UISystem/Forms/ContactFields';
import AboutSection from '../UISystem/Sections/AboutSection';
import InlineSharingList from './InlineSharingList';
import GroupSelector from '../UISystem/GroupSelector';
import useGroups from '../../hooks/useGroups';
import ConnectionDetailShell from './ConnectionDetailShell';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import PhotosSection from '../UISystem/Sections/PhotosSection';
import GroupsSection from '../UISystem/Sections/GroupsSection';
import NicknameField from '../UISystem/Forms/NicknameField';
import useConnectionNote from '../../hooks/useConnectionNote';
import { IconCircleButton } from '../UISystem/Icons/IconCircleButton';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { useConnectionData, useConnectionContext } from '../../contexts/ConnectionStateContext';
import { getMeetingsForConnection, setMeetingVenue } from '../../services/meetings/meetingRows';
import { currentMeetingRow } from '../../services/meetings/meetingRule';
import { persistMessage } from '../../services/chat/messageStore';
import { getInactiveStatus } from '../../utils/anonInactiveStatus';
import { isRoyaleUser } from '../../utils/contactType';
import { getConnectionDisplayName, isActiveRoyalePeer } from '../../utils/connectionName';
import { sharedWithLabel } from '../../utils/contactUtils';
import { applyUserContactChange } from '../../utils/userContactEdits';
import { mergeDetailContacts } from '../../utils/mergeDetailContacts';
import { isPeerGone } from '../../utils/peerGone';
import { getProfileUserId } from '../../utils/identityUtils';
import usePeerStatus from '../../hooks/usePeerStatus';
import ConnectionAvatar from '../UISystem/ConnectionAvatar';
import StatusSubtitle from '../UISystem/StatusSubtitle';
import VenueSelectorMessage from '../Location/VenueSelectorMessage';
import LocationOverlay from '../Overlay/LocationOverlay';
import { sendMessage } from '../../services/chat/chatService';
import { logConnectionLocation } from '../../services/analytics/eventLogger';
import { buildMetadataUpdatedMessage } from '../../services/chat/messageBuilders';
import { saveCustomVenue } from '../../services/location/providers/radarProvider';
import { ButtonRounded } from '../UISystem/Buttons';
import AddCircleButton from '../UISystem/AddCircleButton';
import { DownloadSimple } from '@phosphor-icons/react/dist/ssr/DownloadSimple';
import PhotoLightbox from '../UISystem/Chat/PhotoLightbox';
import { ALL_CONTACT_KEYS } from '../../utils/contactTypeConfig';
import { formatSystemMessageLocation, gateVenueMetadata } from '../../utils/locationFormatters';

/**
 * ProfileConnectionDetailPage - Detailed connection view for profile users
 *
 * This component shows detailed information about an anonymous user
 * from the perspective of a profile user.
 * Gets chatRoomId from session context
 *
 * Redesigned to use grey card layout matching ReceiveContactScreen/ConfirmationScreen
 */
const ProfileConnectionDetailPage = () => {
  const { goBack, navigateTo } = useNavigation();
  const { sessionChatRoomId } = useSession();
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const sharingCommitRef = useRef(null);
  // Page-owned add-contact trigger: the frames place the + below Save.
  const addContactRef = useRef(null);
  const nameInputRef = useRef(null);

  // Meeting rows from DB (stacked WE MET cards)
  const [meetings, setMeetings] = useState([]);
  useEffect(() => {
    const profileUserId = getProfileUserId();
    if (sessionChatRoomId && profileUserId) {
      getMeetingsForConnection(sessionChatRoomId, profileUserId).then(setMeetings);
    }
  }, [sessionChatRoomId]);

  // Swipe-back is disabled while an overlay is open (shell owns useSwipeBack)
  const swipeEnabled = !(showGroupSelector || showLocationOverlay);
  const connectionData = useConnectionData(sessionChatRoomId);
  const {
    updateConnectionMetadata,
    updateChatMessages,
    updateDisplayName,
    updateUserContacts,
    updateNickname,
  } = useConnectionContext();
  const {
    groups,
    getConnectionViewId,
    getGroupsForConnection,
    actions: groupActions,
  } = useGroups();

  // ONE note per connection (2026-08-18 Detail frames): Note and Nickname were
  // two fields on the same connection_views row. nickname is the survivor; the
  // legacy notes column drains into it on first open. Must sit above the render
  // prop — hooks can't run inside it.
  const noteText = useConnectionNote(sessionChatRoomId, connectionData, true);

  const nameInitializedRef = useRef(false);

  const handleBack = () => {
    goBack('profile-connections-list');
  };

  // Inactivity status — the page's one notion of "can this channel still
  // deliver". peerProfileDeleted already resolves to 'inactive' here, so the
  // deleted peer loses the corner action without a second predicate.
  const inactiveStatus = getInactiveStatus(connectionData || {});
  const canChat = inactiveStatus !== 'inactive';

  // Peer activity + live location for header subtitle (no heartbeat on detail page)
  const { activityStatus, peerLocation } = usePeerStatus(
    connectionData?.anonData?.senderProfileUserId,
    sessionChatRoomId
  );

  const handleChatClick = () => {
    navigateTo('profile-connection-page', {
      skipInitialOverlay: true,
      source: 'connection-detail',
    });
  };

  // Handle clicking on name to edit
  const handleNameClick = (currentName) => {
    setEditedName(currentName || '');
    setIsNameFocused(true);
    // Focus the input after state update
    requestAnimationFrame(() => {
      nameInputRef.current?.focus();
    });
  };

  return (
    <ConnectionDetailPage
      chatRoomId={sessionChatRoomId}
      isProfileUser={true} // Profile user viewing anon data
    >
      {({ userData, connectionMetadata, onAddToContacts, chatMessages }) => {
        // Initialize editedName via the shared precedence (connectionName.js):
        // editable connections get the override-first name, active Royale peers the live name
        if (
          !nameInitializedRef.current &&
          (connectionData?.displayName || userData?.name !== undefined)
        ) {
          nameInitializedRef.current = true;
          setEditedName(
            getConnectionDisplayName({
              displayName: connectionData?.displayName,
              anonData: userData,
            }) || ''
          );
        }

        const isPXP = !!userData?.senderProfileUserId;

        // Peer gone (CD #15): account-deleted OR left this connection. You take
        // full ownership — their shared contacts are KEPT and become editable
        // (mergeDetailContacts no longer drops them), photo + name persist for
        // identification, and the outbound "sharing with them" section is hidden
        // (no live peer to share with). Covers both cases via isPeerGone.
        const peerGone = isPeerGone(userData);
        // Name is editable for anon and deleted-profile contacts, but not active Royale
        // peers — same predicate as the display precedence, so they can't drift.
        const nameEditable = !isActiveRoyalePeer(userData);
        // Saved-name baseline for dirty checks — same shared precedence as display
        const savedName =
          getConnectionDisplayName({
            displayName: connectionData?.displayName,
            anonData: userData,
          }) || '';
        // Active Royale peers render the LIVE shared name (share changes appear
        // immediately, like contact fields); editable connections use the edit buffer.
        const displayedName = nameEditable ? editedName : userData?.name || '';

        const handleSaveAndBack = async () => {
          // No note save here. The one field saves on its own blur, and
          // ConnectionDetailPage seeds its note state once at mount — calling
          // saveNote() on the way out would write that pre-merge text straight
          // back over a connection the migration had just drained.
          // Save name via displayName (Profile's local override, not DB-owned anonData).
          // Gated: an active Royale peer owns their name — never write an override for
          // them (a stale buffer here is how names froze; see record-integrity plan).
          const trimmedName = editedName.trim();
          if (nameEditable && trimmedName !== savedName) {
            updateDisplayName(sessionChatRoomId, trimmedName);
          }
          // User-added contacts persist to connection_views.user_contacts on edit (DB-first).
          // Commit any SHARED toggles (commit-on-leave; no-op if nothing changed).
          await sharingCommitRef.current?.();
          handleBack();
        };

        // CONTACTS — claiming is the gate; the merge rule lives in mergeDetailContacts.
        const peerHasClaimed = isActiveRoyalePeer(userData);
        const userContacts = connectionData?.userContacts || {};
        const { mergedContacts, readOnlyContactKeys, peerShared } = mergeDetailContacts({
          userData,
          userContacts,
          peerHasClaimed,
          contactKeys: ALL_CONTACT_KEYS,
        });

        return (
          <>
            <ConnectionDetailShell
              onBack={handleSaveAndBack}
              swipeEnabled={swipeEnabled}
              topRight={
                canChat ? (
                  <IconCircleButton
                    icon={PaperPlaneTilt}
                    size={22}
                    buttonSize={40}
                    onClick={handleChatClick}
                    iconColor="#555555"
                  />
                ) : null
              }
            >
              {/* Profile Content Block - positioned from top */}
              <div
                style={{
                  paddingTop: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                }}
              >
                {/* Profile Photo */}
                <ConnectionAvatar
                  photoUrl={userData?.photoUrl}
                  name={displayedName}
                  isRoyaleUser={isRoyaleUser(userData)}
                  placeholderBg="#C7C7CC"
                  initialColor="#FFFFFF"
                  fallbackIcon={<User size={40} color="#FFFFFF" weight="regular" />}
                />

                {/* Name - click to edit */}
                <div
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {isNameFocused ? (
                    <NameInput
                      ref={nameInputRef}
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onBlur={() => {
                        const trimmedName = editedName.trim();
                        if (nameEditable && trimmedName && trimmedName !== savedName) {
                          updateDisplayName(sessionChatRoomId, trimmedName);
                        }
                        setIsNameFocused(false);
                      }}
                    />
                  ) : (
                    <div
                      onClick={nameEditable ? () => handleNameClick(editedName) : undefined}
                      style={{
                        fontSize: '24px',
                        fontWeight: displayedName ? '700' : '500',
                        color: displayedName ? '#000000' : '#8E8E93',
                        letterSpacing: '-0.3px',
                        textAlign: 'center',
                        cursor: nameEditable ? 'pointer' : 'default',
                        padding: '8px 16px',
                        borderRadius: '16px',
                        transition: 'background 0.2s ease',
                      }}
                    >
                      {displayedName || (nameEditable ? 'Add name' : '')}
                    </div>
                  )}
                </div>

                {/* Status subtitle — live location or activity only (hometown lives in
                    ABOUT). Rendered only when it has content, so the standard view keeps
                    the icons tight under the name (the contacts associate with the name). */}
                {(() => {
                  const isAnon = !isPXP;
                  // Anon users: show activity only < 24h, nothing 24h+
                  const filteredActivity =
                    activityStatus && !(isAnon && inactiveStatus && inactiveStatus !== 'active')
                      ? activityStatus
                      : null;

                  if (!peerLocation && !filteredActivity) return null;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '2px',
                      }}
                    >
                      <StatusSubtitle
                        locationDisplay={peerLocation}
                        activityStatus={filteredActivity}
                        size={14}
                      />
                    </div>
                  );
                })()}

                {/* No contact icon row at the top (user, 2026-08-20). The
                    contacts list below is the ONE place to see and tap a
                    channel — the icons up here were a second, partial copy of
                    the same information. This matches the Anon and Manual
                    pages, which never had one. */}
              </div>

              {/* Main content area — single flex gap drives the spacing between every
                      section, so the vertical rhythm is uniform (each section drops its own
                      marginBottom; content trailing margins are zeroed where they'd add). */}
              <div
                style={{
                  flex: 1,
                  padding: '20px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                }}
              >
                {/* GROUPS — colored chips + add button (opens GroupSelector) */}
                <GroupsSection
                  groups={getGroupsForConnection(sessionChatRoomId)}
                  onEdit={() => setShowGroupSelector(true)}
                />

                {/* NOTE — the one note field (Note + Nickname merged, 2026-08-18):
                    how I know them, viewer-private, DB-first on
                    connection_views.nickname. Still feeds the People card's
                    nickname line (Plan 9). */}
                <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                  <NicknameField
                    value={noteText}
                    onSave={(text) => updateNickname(sessionChatRoomId, text)}
                  />
                </div>

                {/* WE MET — stacked cards from meetings table, fallback to connectionMetadata */}
                {(meetings.length > 0 || connectionMetadata) && (
                  <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                    {/* Flex gap stacks multiple meetings without leaving a trailing margin */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {meetings.length > 0
                        ? meetings.map((meeting) => {
                            // met_at (full instant) when present; else met_date renders
                            // as a plain calendar date — no time, no UTC-midnight math
                            const meetingMetadata = {
                              locationData: { venueInfo: meeting.location_data || {} },
                              meetingDate: meeting.met_at || meeting.met_date,
                              meetingUtcOffset: meeting.met_utc_offset,
                              location: meeting.venue_name,
                            };
                            return (
                              <div
                                key={meeting.id}
                                onClick={() => setShowLocationOverlay(true)}
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: '16px',
                                  boxShadow: SHADOWS.TIER1,
                                  overflow: 'hidden',
                                }}
                              >
                                <VenueSelectorMessage
                                  connectionMetadata={meetingMetadata}
                                  isEditable={false}
                                  showAddPlaceInvite={true}
                                  showPin={false}
                                  composePlace
                                  showAddress={false}
                                  backgroundColor="rgba(255, 255, 255, 0.95)"
                                  showDateTime={true}
                                  maxWidth={340}
                                  align="center"
                                />
                              </div>
                            );
                          })
                        : connectionMetadata && (
                            <div
                              onClick={() => setShowLocationOverlay(true)}
                              style={{
                                cursor: 'pointer',
                                borderRadius: '16px',
                                boxShadow: SHADOWS.TIER1,
                                overflow: 'hidden',
                              }}
                            >
                              {/* Venue gate: an SP guest reads this surface
                                  too — an unconfirmed host venue must fall to
                                  the neighborhood (device pass 2) */}
                              <VenueSelectorMessage
                                connectionMetadata={gateVenueMetadata(connectionMetadata)}
                                isEditable={false}
                                showAddPlaceInvite={true}
                                showPin={false}
                                composePlace
                                showAddress={false}
                                backgroundColor="rgba(255, 255, 255, 0.95)"
                                showDateTime={true}
                                maxWidth={340}
                                align="center"
                              />
                            </div>
                          )}
                    </div>
                  </div>
                )}

                {/* PHOTOS */}
                <PhotosSection messages={chatMessages} onOpen={setLightboxUrl} />

                {/* ABOUT — one card: where they live, role/company, what
                    they're up to, places they like (user, 2026-08-20).
                    Hidden for a gone peer (deleted/left, CD #15, Mark 2026-10-07):
                    it's a frozen snapshot that can't update, so we don't show it —
                    contacts stay (yours, editable); About does not. */}
                {!peerGone && <AboutSection userData={userData} />}

                {/* CONTACTS — peer's shared fields (read-only) + your additions (editable) */}
                <div style={{ width: '100%', maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH }}>
                  <ContactFields
                    userData={mergedContacts}
                    readOnlyFields={readOnlyContactKeys}
                    showLinks={true}
                    compact={true}
                    // The + lives below Save to contacts on the frames, so the
                    // page places it and this list just hands over the opener.
                    hideAddButton={true}
                    addRef={addContactRef}
                    starterFields={[]}
                    onChange={(patch) => {
                      // Fold the whole patch in one pass. A promotion arrives as
                      // both fields together (ContactFields.handleFieldClear), so
                      // there is no second call to race — the mutable ref that
                      // used to carry state between the two calls is gone.
                      const next = Object.entries(patch).reduce(
                        (acc, [fieldKey, value]) =>
                          applyUserContactChange(acc, fieldKey, value, {
                            peerShared,
                            peerHasClaimed,
                          }),
                        userContacts || {}
                      );
                      updateUserContacts(sessionChatRoomId, next);
                    }}
                  />
                </div>

                {/* Save sits tight under the last contact row (frame: ~8px, against
                    the column's 20px rhythm) — it belongs to the rows it acts on. */}
                {/* Save to contacts — vCard download, below the contact rows and
                        matched to their width. Black CTA until tapped, then white — we can
                        only detect the tap (it hands off to the iOS contact card), not
                        whether it was actually saved. */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '-12px',
                  }}
                >
                  <ButtonRounded
                    text="Save to contacts"
                    icon={DownloadSimple}
                    onClick={onAddToContacts}
                    size="compact"
                    variant={connectionData?.hasAddedToContacts ? 'secondary' : 'primary'}
                    style={{ width: DETAIL_TOKENS.ROW_MAX_WIDTH }}
                  />
                  {/* Chat archive — when the channel can no longer deliver, the
                      corner action is gone and the history hangs here instead
                      (profile_anon_2). Left-aligned on the contact rows' grid,
                      not centred: the frame lines its icon up with their icons
                      and its label with their values. No date — the plan's prose
                      said "dated", the frame doesn't, and the frames win. */}
                  {!canChat && (
                    <button
                      type="button"
                      onClick={handleChatClick}
                      style={{
                        width: DETAIL_TOKENS.ROW_MAX_WIDTH,
                        padding: '12px 16px',
                        background: 'none',
                        border: 'none',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <span
                        style={{
                          width: '36px',
                          display: 'flex',
                          justifyContent: 'center',
                          marginRight: '8px',
                          flexShrink: 0,
                        }}
                      >
                        <PaperPlaneTilt size={22} color={COLORS.DARK_GRAY} weight="regular" />
                      </span>
                      <span style={{ fontSize: '17px', color: COLORS.DARK_GRAY }}>
                        Chat archive
                      </span>
                    </button>
                  )}

                  {/* Add a contact of your own for this person — bare + per the frames */}
                  <AddCircleButton
                    label="Add contact"
                    showLabel={false}
                    onClick={() => addContactRef.current?.()}
                  />
                </div>

                {/* SHARED — what you're sharing with this person; edit inline.
                        Hidden for a gone peer (deleted OR left, CD #15) — there's
                        no live peer to share with. */}
                {!peerGone && (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: DETAIL_TOKENS.SECTION_MAX_WIDTH,
                      // A deliberate break: what you share is a separate idea
                      // from what they shared (frame: ~48px vs the 20px rhythm)
                      marginTop: '28px',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#8E8E93',
                        textAlign: 'center',
                        marginBottom: '10px',
                      }}
                    >
                      {sharedWithLabel(displayedName)}
                    </div>
                    <InlineSharingList
                      chatRoomId={sessionChatRoomId}
                      commitRef={sharingCommitRef}
                    />
                  </div>
                )}

                {/* Bottom padding - ensures content is scrollable past safe areas */}
                <div style={{ height: '40px', flexShrink: 0 }} />
              </div>
            </ConnectionDetailShell>

            {/* Photo lightbox */}
            {lightboxUrl && (
              <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
            )}

            {/* Group selector overlay */}
            <AnimatePresence>
              {showGroupSelector && (
                <GroupSelector
                  groups={groups}
                  selectedGroupIds={getGroupsForConnection(sessionChatRoomId).map((g) => g.id)}
                  onSave={(groupIds) => {
                    const viewId = getConnectionViewId(sessionChatRoomId);
                    if (viewId) groupActions.setConnectionGroups(viewId, groupIds);
                  }}
                  onClose={() => setShowGroupSelector(false)}
                  onCreateGroup={groupActions.createCustomGroup}
                  onDeleteGroup={groupActions.deleteCustomGroup}
                />
              )}
            </AnimatePresence>

            {/* Location overlay */}
            {showLocationOverlay && connectionMetadata && (
              <LocationOverlay
                nearbyVenues={connectionMetadata?.locationData?.venueInfo?.nearbyVenues || []}
                currentVenue={connectionMetadata?.locationData?.venueInfo?.venue || null}
                address={connectionMetadata?.locationData?.venueInfo?.address || {}}
                coords={connectionMetadata?.locationData?.coords || {}}
                showLocationContext={false}
                onVenueChange={async (selectedVenue) => {
                  // Update venue in connectionMetadata
                  const address = connectionMetadata?.locationData?.venueInfo?.address || {};
                  const coords = connectionMetadata?.locationData?.coords || {};

                  // Save custom venue to localStorage for future lookups at this location
                  if (
                    selectedVenue?.isCustom &&
                    selectedVenue?.name &&
                    coords.latitude &&
                    coords.longitude
                  ) {
                    saveCustomVenue(selectedVenue.name, coords.latitude, coords.longitude);
                  }

                  const updatedMetadata = {
                    ...connectionMetadata,
                    venueConfirmed: true,
                    locationData: {
                      ...connectionMetadata?.locationData,
                      venueInfo: {
                        ...connectionMetadata?.locationData?.venueInfo,
                        venue: selectedVenue,
                      },
                    },
                  };

                  // Update the location string
                  let locationString = '';
                  if (selectedVenue?.name) {
                    const addressParts = [];
                    if (address.streetNumber && address.street) {
                      addressParts.push(`${address.streetNumber} ${address.street}`);
                    } else if (address.street) {
                      addressParts.push(address.street);
                    }
                    if (address.cityAbbr || address.city) {
                      addressParts.push(address.cityAbbr || address.city);
                    }
                    locationString = `${selectedVenue.name}${addressParts.length > 0 ? ', ' + addressParts.join(', ') : ''}`;
                  } else {
                    const addressParts = [];
                    if (address.streetNumber && address.street) {
                      addressParts.push(`${address.streetNumber} ${address.street}`);
                    } else if (address.street) {
                      addressParts.push(address.street);
                    }
                    if (address.neighborhood) {
                      addressParts.push(address.neighborhood);
                    }
                    if (address.cityAbbr || address.city) {
                      addressParts.push(address.cityAbbr || address.city);
                    }
                    locationString = addressParts.join(', ');
                  }
                  updatedMetadata.location = locationString;

                  // Write 1: Update connectionMetadata in context + localStorage (Card surface)
                  await updateConnectionMetadata(sessionChatRoomId, updatedMetadata);

                  // Write 2: Sync meeting row in DB (Detail page surface)
                  setMeetingVenue(
                    sessionChatRoomId,
                    getProfileUserId(),
                    updatedMetadata.meetingKey,
                    updatedMetadata.locationData.venueInfo
                  );

                  // Mirror Write 2 into local state — the WE MET card renders from
                  // mount-time meetings state and the DB write is fire-and-forget,
                  // so without this the card stays stale until the page remounts.
                  // Same target row as setMeetingVenue: the record's meeting key,
                  // else the newest key-less row (the list is newest first).
                  setMeetings((prev) => {
                    const latest = currentMeetingRow(prev, updatedMetadata.meetingKey);
                    if (!latest) return prev;
                    const venueInfo = updatedMetadata.locationData.venueInfo;
                    return prev.map((m) =>
                      m.id === latest.id
                        ? {
                            ...m,
                            venue_name: venueInfo?.venue?.name || null,
                            venue_address: venueInfo?.address?.formattedAddress || null,
                            location_data: venueInfo,
                          }
                        : m
                    );
                  });

                  // Write 3: Update own system message in chat (Chat surface)
                  const userId = getProfileUserId();
                  const newContent = formatSystemMessageLocation(updatedMetadata);
                  if (newContent && chatMessages) {
                    const ownSystemMessages = chatMessages.filter(
                      (msg) => msg.type === 'system' && msg.id?.endsWith(`_${userId}`)
                    );
                    if (ownSystemMessages.length > 0) {
                      const latest = ownSystemMessages[ownSystemMessages.length - 1];
                      if (newContent !== latest.content) {
                        const updated = chatMessages.map((msg) =>
                          msg.id === latest.id ? { ...msg, content: newContent } : msg
                        );
                        updateChatMessages(sessionChatRoomId, updated);
                        persistMessage(sessionChatRoomId, { ...latest, content: newContent });
                      }
                    } else {
                      // Old-format fallback (pre-Phase 2)
                      const oldMsg = chatMessages.find(
                        (msg) => msg.type === 'system' && msg.id === `system_${sessionChatRoomId}`
                      );
                      if (oldMsg) {
                        const oldDate = oldMsg.content?.split('\n')[1];
                        const updatedContent = [newContent, oldDate].filter(Boolean).join('\n');
                        const updated = chatMessages.map((msg) =>
                          msg.id === oldMsg.id ? { ...msg, content: updatedContent } : msg
                        );
                        updateChatMessages(sessionChatRoomId, updated);
                        persistMessage(sessionChatRoomId, {
                          ...oldMsg,
                          content: updatedContent,
                        });
                      }
                    }
                  }

                  // Log analytics
                  logConnectionLocation(sessionChatRoomId, updatedMetadata.locationData, 'manual');

                  // PXP: venue is per-user, don't broadcast.
                  // Anon exchanges: venue is shared, sync via WebSocket.
                  if (!isPXP) {
                    try {
                      await sendMessage(
                        sessionChatRoomId,
                        buildMetadataUpdatedMessage({
                          connectionMetadata: updatedMetadata,
                          senderProfileUserId: getProfileUserId(),
                        })
                      );
                    } catch (error) {
                      console.error(
                        '[ProfileConnectionDetailPage] Error sending metadata_updated:',
                        error
                      );
                    }
                  }

                  setShowLocationOverlay(false);
                }}
                onClose={() => setShowLocationOverlay(false)}
              />
            )}
          </>
        );
      }}
    </ConnectionDetailPage>
  );
};

export default ProfileConnectionDetailPage;
